'use client';

import React, { useState } from 'react';

import RichText from '@/components/ui/RichText/RichText';
import type { CardsProps } from '@/features/healthPackages/types';

import { resolveLabels } from './defaultLabels';
import { getDisplayPrice, getTestsMeta } from './utils';
import CardHeader from './CardHeader';
import CardMetaInfo from './CardMetaInfo';
import IncludedTestsHeader from './IncludedTestsHeader';
import GroupedTestsList from './GroupedTestsList';
import FlatTestsList from './FlatTestsList';
import DescriptionTestsList from './DescriptionTestsList';
import CardFooter from './CardFooter';

export default function PackageCard({
  badge,
  title,
  subtitle,
  tests,
  testGroups,
  descriptionText,
  subDescription,
  testType,
  gender,
  price,
  description,
  button,
  defaultOpen = false,
  text4 = '',
  disclaimer = '',
  labels,
  presentation,
  onEnquire = (): void => {},
  onBook,
  onToggleOpen,
}: CardsProps): React.ReactElement {
  const [open, setOpen] = useState(defaultOpen);
  const [openGroups, setOpenGroups] = useState<Set<number>>(() => new Set());

  const handleToggleOpen = (): void => {
    const next = !open;
    setOpen(next);
    onToggleOpen?.(next || openGroups.size > 0);
  };

  const toggleGroup = (index: number): void => {
    const next = new Set(openGroups);
    if (next.has(index)) next.delete(index);
    else next.add(index);
    setOpenGroups(next);
    onToggleOpen?.(open || next.size > 0);
  };

  const l = resolveLabels(labels);
  const showItemCount = presentation?.showItemCount ?? true;
  const showReferral = presentation?.showReferral ?? true;
  const showGender = presentation?.showGender ?? true;
  const showIncludedItemsHeader = presentation?.showIncludedItemsHeader ?? true;
  const showPrice = presentation?.showPrice ?? true;
  const showEnquireAction = presentation?.showEnquireAction ?? true;

  const hasGroups = Array.isArray(testGroups) && testGroups.length > 0;
  const isFlat = Array.isArray(tests);

  const { showToggle, testsCount } = getTestsMeta({
    hasGroups,
    isFlat,
    tests,
    testGroups,
    subDescription,
    description,
  });

  const { priceText } = getDisplayPrice(price, {
    currency: presentation?.currency ?? '£',
    isFormatted: presentation?.priceIsFormatted ?? false,
    label: l.priceLabel?.content,
  });

  return (
    <div
      data-card
      className="mb-3 flex h-full flex-col rounded-2xl border border-slate-100 bg-white p-6 shadow-md"
    >
      <CardHeader
        badge={badge}
        title={title}
        subtitle={subtitle}
        testsCount={testsCount}
        showItemCount={showItemCount}
        labels={l}
      />

      <CardMetaInfo
        testType={testType}
        gender={gender}
        labels={l}
        showReferral={showReferral}
        showGender={showGender}
      />

      {text4 && <RichText styles={l.text4?.styles}>{text4}</RichText>}

      {showIncludedItemsHeader && (
        <IncludedTestsHeader
          open={open}
          showToggle={showToggle}
          onToggle={handleToggleOpen}
          labels={l}
        />
      )}

      <div className="mt-2 ml-2 flex-1">
        {hasGroups ? (
          <GroupedTestsList
            testGroups={testGroups!}
            openGroups={openGroups}
            onToggleGroup={toggleGroup}
            cardOpen={open}
          />
        ) : isFlat ? (
          <FlatTestsList
            tests={tests!}
            descriptionText={descriptionText}
            cardOpen={open}
            labels={l}
          />
        ) : (
          <DescriptionTestsList
            subDescription={subDescription}
            description={description}
            cardOpen={open}
          />
        )}
      </div>

      {disclaimer && (
        <RichText styles={l.disclaimer?.styles}>{disclaimer}</RichText>
      )}

      <CardFooter
        button={button}
        onEnquire={onEnquire}
        onBook={onBook}
        priceText={showPrice ? priceText : null}
        labels={l}
        showEnquireAction={showEnquireAction}
      />
    </div>
  );
}
